import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TileList,
  Typography,
  useClientOptions,
  SherwinWilliamsLogo
} from '@paintscout/ui'
import { createPresentationSection, getFeature, getPresentationSectionOptions } from '@paintscout/util/builder'
import type { PartnerContentFeatures, PresentationSection } from 'paintscout'
import React, { useState } from 'react'
import InfoIcon from '@material-ui/icons/Info'
import sections from '../../sections'

export interface PresentationSectionSelectDialogProps extends Omit<DialogProps, 'message' | 'title' | 'loading'> {
  classes?: StyleClasses<typeof useStyles>

  onConfirm?: (section: PresentationSection) => void
  onCancel?: () => void
}

const useStyles = makeStyles<Theme, PresentationSectionSelectDialogProps>(
  (theme) => ({
    root: {},
    tileList: {},
    contactDocsButtonContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      width: '100%',
      alignItems: 'center',
      gap: theme.spacing(2),
      overflow: 'hidden',
      overflowX: 'auto',
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(2),
      paddingLeft: theme.spacing(2)
    },
    alert: { display: 'flex', alignItems: 'center' },
    icon: {
      marginRight: theme.spacing(1),
      alignSelf: 'flex-start',
      color: theme.palette.info.main
    },
    sherwinWilliamsLogo: {
      width: '90px',
      height: '20px'
    }
  }),
  { name: 'PresentationSectionSelectDialog' }
)

function PresentationSectionSelectDialog(props: PresentationSectionSelectDialogProps) {
  const { open, onConfirm, onCancel } = props
  const { options } = useClientOptions()
  const classes = useStyles(props)

  const partnerContentFeatures: PartnerContentFeatures = getFeature({ options, path: 'partnerContent' })
  const [tabs, setTabs] = useState(
    [
      { label: 'Defaults', value: 'default', active: true },
      partnerContentFeatures?.enabled && partnerContentFeatures?.sherwinWilliams?.presentationPages?.enabled
        ? {
            label: 'Sherwin-Williams',
            value: 'sherwin-williams',
            active: false
          }
        : null,
      partnerContentFeatures?.enabled && partnerContentFeatures?.benjaminMoore?.presentationPages?.enabled
        ? {
            label: 'Benjamin Moore',
            value: 'benjamin-moore',
            active: false
          }
        : null
    ].filter(Boolean)
  )

  const sectionValue = tabs.find((tab) => tab.active).value
  const sectionOptions = getPresentationSectionOptions({
    options,
    type: sectionValue,
    skipSort: sectionValue !== 'default'
  }).map((section) => ({
    ...section,
    Thumbnail: sections?.[section.type]?.Thumbnail
  }))

  const handleTabClick = (value: string) => {
    setTabs((tabs) =>
      tabs.map((tab) => ({
        ...tab,
        active: tab.value === value
      }))
    )
  }

  return (
    <Dialog open={open} {...props}>
      <DialogTitle>Add Section</DialogTitle>
      <div className={classes.contactDocsButtonContainer}>
        {tabs.length > 1 &&
          tabs.map((tab) => (
            <div key={tab.value}>
              <Button variant="tab" onClick={() => handleTabClick(tab.value)} size="small" active={tab.active}>
                {tab.label === 'Sherwin-Williams' ? (
                  <SherwinWilliamsLogo fill={tab.active ? '#FFFFFF' : null} className={classes.sherwinWilliamsLogo} />
                ) : (
                  tab.label
                )}
              </Button>
            </div>
          ))}
      </div>
      <DialogContent>
        <div className={classes.root}>
          {sectionValue !== 'default' && (
            <Alert icon={null} severity={'info'} onClose={null}>
              <div className={classes.alert}>
                <div className={classes.icon}>
                  <InfoIcon />
                </div>
                <Typography variant="body1">
                  The sections below include ready-made content provided by {tabs.find((tab) => tab.active)?.label}, and
                  cannot be edited.
                </Typography>
              </div>
            </Alert>
          )}
          <TileList
            variant="standard"
            items={sectionOptions.map((section, index) => ({ ...section, key: index }))}
            onSelect={onTileClick}
          />
        </div>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="textNoPadding" onClick={onCancel}>
            Cancel
          </Button>
        }
      ></DialogActions>
    </Dialog>
  )

  function onTileClick(event: any, index: number) {
    const presentationSectionOption = sectionOptions[index]
    delete presentationSectionOption.Thumbnail
    if (onConfirm && presentationSectionOption) {
      onConfirm(createPresentationSection({ presentationSectionOption }))
    }
  }
}

export default PresentationSectionSelectDialog
